import { INSTALLMENT_BILLING, TODAY } from '../../constants';
import type { DealLine } from '../../types/domain';
import { contractTotalMonths, OPEN_CONTRACT_MONTHS } from '../dates';
import { toNumber } from '../numbers';

const filledMonths = (v: unknown) => (v != null && v !== '' ? toNumber(v) || 0 : 0);

// Contrat récurrent sans date de fin = reconduction tacite (règle du 2026-10-02) : la première
// période (engagement, sinon 12 mois) compte en entier, puis le contrat repart pour un an tant
// qu'il n'est pas clôturé. Le CA compte les mois jusqu'à la fin de l'année en cours, jamais les
// années futures : une maintenance du 01/01/2025 compte 12 mois en 2025, 12 en 2026, et 2027
// seulement une fois 2027 commencée.
export function renewedOpenMonths(
  startIso: string | null | undefined,
  initialMonths: number,
  today: Date = TODAY,
): number {
  return Math.max(initialMonths, contractTotalMonths(startIso, `${today.getFullYear()}-12-31`));
}

// Première période d'un contrat sans date de fin : l'engagement vendu, sinon 12 mois (règle 1.1).
export const soldInitialMonths = (l: DealLine): number =>
  filledMonths(l.engagement_mois_vendu) || OPEN_CONTRACT_MONTHS;

// Mois vendus d'un contrat sans date de fin, reconductions comprises. Une date de fin, quand elle
// existe, prime toujours (la clôture de la phase la pose et arrête la reconduction). Un forfait
// échelonné s'arrête à sa dernière mensualité : jamais reconduit.
export const soldOpenMonths = (l: DealLine, today: Date = TODAY): number =>
  l.facturation === INSTALLMENT_BILLING
    ? soldInitialMonths(l)
    : renewedOpenMonths(l.date_debut_prevue, soldInitialMonths(l), today);

// Modes dont la fiche affiche un engagement estimé (bloc « Estimé ») : maintenances et régies.
// Pour les autres (hébergement, forfait échelonné), un engagement estimé resté en base après un
// changement de mode est invisible : il ne doit ni compter ni bloquer.
const ESTIMATED_COMMITMENT_BILLING = [
  'maintenance_corrective',
  'maintenance_evolutive',
  'regie_temporelle',
  'regie_horaire',
];
export const estimatedCommitment = (l: DealLine): number =>
  ESTIMATED_COMMITMENT_BILLING.includes(l.facturation as string)
    ? filledMonths(l.engagement_mois_estime)
    : 0;

// Potentiel (affaire pas encore signée) : pas de reconduction, l'engagement estimé, sinon vendu,
// sinon 12 mois.
export const estimatedOpenMonths = (l: DealLine): number =>
  estimatedCommitment(l) || filledMonths(l.engagement_mois_vendu) || OPEN_CONTRACT_MONTHS;
